import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mockUser, mockStats, mockMessages, mockActivity, mockEnrolledPrograms } from "@/mocks/dashboard";

export default function DashboardPage() {
  const [activeTab, setActiveTab] = useState<"overview" | "messages" | "programs" | "activity">("overview");
  const [messageText, setMessageText] = useState("");
  const [selectedMsg, setSelectedMsg] = useState(mockMessages[0]);

  return (
    <div className="min-h-screen bg-[#F7F6F3]">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Profile Header */}
          <div className="bg-white rounded-2xl border border-gray-100 p-8 mb-6 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="relative">
              <img
                src={mockUser.avatar}
                alt={mockUser.name}
                className="w-20 h-20 rounded-full object-cover object-top border-4 border-teal-100"
              />
              <span className="absolute bottom-0 right-0 w-5 h-5 bg-teal-500 rounded-full border-2 border-white" />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {mockUser.name}
                </h1>
                <span className="bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                  {mockUser.role}
                </span>
              </div>
              <p className="text-gray-500 text-sm mb-2">{mockUser.niche} · {mockUser.location}</p>
              <p className="text-gray-600 text-sm max-w-xl">{mockUser.bio}</p>
              <div className="flex gap-6 mt-3">
                <div className="text-center">
                  <p className="font-bold text-gray-900 text-sm">{mockUser.followers.toLocaleString()}</p>
                  <p className="text-gray-400 text-xs">Followers</p>
                </div>
                <div className="text-center">
                  <p className="font-bold text-gray-900 text-sm">{mockUser.following}</p>
                  <p className="text-gray-400 text-xs">Following</p>
                </div>
                <div className="text-center">
                  <p className="font-bold text-gray-900 text-sm">{mockUser.rating}★</p>
                  <p className="text-gray-400 text-xs">Rating</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button className="px-5 py-2 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                Edit Profile
              </button>
              <Link
                to="/payment"
                className="px-5 py-2 bg-teal-600 text-white text-sm font-medium rounded-xl hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Monetize
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {mockStats.map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${stat.bg}`}>
                  <i className={`${stat.icon} text-xl ${stat.color}`} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-gray-400 text-xs">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-white border border-gray-100 rounded-xl p-1 mb-6 w-fit">
            {(["overview", "messages", "programs", "activity"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap capitalize ${
                  activeTab === tab ? "bg-teal-600 text-white" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Enrolled Programs */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="font-semibold text-gray-900 mb-5">My Programs</h2>
                <div className="space-y-4">
                  {mockEnrolledPrograms.map((prog) => (
                    <div key={prog.id} className="flex items-center gap-4">
                      <img src={prog.cover} alt={prog.title} className="w-16 h-12 rounded-lg object-cover object-top flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{prog.title}</p>
                        <p className="text-xs text-gray-400">{prog.coach}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-teal-500 rounded-full"
                              style={{ width: `${prog.progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">{prog.progress}%</span>
                        </div>
                      </div>
                      <button className="text-xs text-teal-600 font-medium cursor-pointer whitespace-nowrap hover:text-teal-700">
                        Continue
                      </button>
                    </div>
                  ))}
                </div>
                <Link
                  to="/programs"
                  className="mt-5 block text-center text-sm text-teal-600 font-medium hover:text-teal-700 cursor-pointer"
                >
                  Browse More Programs →
                </Link>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="font-semibold text-gray-900 mb-5">Recent Activity</h2>
                <div className="space-y-4">
                  {mockActivity.map((a) => (
                    <div key={a.id} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                        <i className={`${a.icon} text-teal-600 text-sm`} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-700 leading-relaxed">{a.text}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{a.timeAgo}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === "messages" && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="flex h-[500px]">
                {/* Sidebar */}
                <div className="w-72 border-r border-gray-100 flex flex-col">
                  <div className="p-4 border-b border-gray-100">
                    <h2 className="font-semibold text-gray-900 text-sm">Messages</h2>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {mockMessages.map((msg) => (
                      <button
                        key={msg.id}
                        onClick={() => setSelectedMsg(msg)}
                        className={`w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50 ${
                          selectedMsg.id === msg.id ? "bg-teal-50" : ""
                        }`}
                      >
                        <div className="relative flex-shrink-0">
                          <img src={msg.avatar} alt={msg.sender} className="w-10 h-10 rounded-full object-cover object-top" />
                          {msg.unread && (
                            <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-teal-500 rounded-full border-2 border-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <p className={`text-xs font-semibold truncate ${msg.unread ? "text-gray-900" : "text-gray-600"}`}>
                              {msg.sender}
                            </p>
                            <span className="text-xs text-gray-400 whitespace-nowrap ml-2">{msg.timeAgo}</span>
                          </div>
                          <p className="text-xs text-gray-400 truncate">{msg.preview}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 flex flex-col">
                  <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                    <img src={selectedMsg.avatar} alt={selectedMsg.sender} className="w-9 h-9 rounded-full object-cover object-top" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{selectedMsg.sender}</p>
                      <p className="text-xs text-teal-500">Online</p>
                    </div>
                  </div>
                  <div className="flex-1 p-6 overflow-y-auto">
                    <div className="flex justify-start mb-4">
                      <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-xs">
                        <p className="text-sm text-gray-700">{selectedMsg.preview}</p>
                        <p className="text-xs text-gray-400 mt-1">{selectedMsg.timeAgo}</p>
                      </div>
                    </div>
                    <div className="flex justify-end mb-4">
                      <div className="bg-teal-600 rounded-2xl rounded-tr-sm px-4 py-3 max-w-xs">
                        <p className="text-sm text-white">Thanks so much! I&apos;d love to connect and discuss further.</p>
                        <p className="text-xs text-teal-200 mt-1">Just now</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 border-t border-gray-100 flex gap-3">
                    <input
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400"
                    />
                    <button
                      onClick={() => setMessageText("")}
                      className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-xl hover:bg-teal-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-send-plane-line text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Programs Tab */}
          {activeTab === "programs" && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-semibold text-gray-900">My Enrolled Programs</h2>
                <Link to="/programs" className="text-sm text-teal-600 font-medium cursor-pointer whitespace-nowrap">
                  Browse More →
                </Link>
              </div>
              <div className="space-y-5">
                {mockEnrolledPrograms.map((prog) => (
                  <div key={prog.id} className="flex items-center gap-5 p-4 border border-gray-100 rounded-xl">
                    <img src={prog.cover} alt={prog.title} className="w-24 h-16 rounded-lg object-cover object-top flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm mb-1">{prog.title}</p>
                      <p className="text-xs text-gray-400 mb-3">by {prog.coach}</p>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-500 rounded-full" style={{ width: `${prog.progress}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">{prog.progress}% complete</span>
                      </div>
                    </div>
                    <button className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                      Continue
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === "activity" && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-semibold text-gray-900 mb-6">Activity History</h2>
              <div className="space-y-4">
                {mockActivity.map((a) => (
                  <div key={a.id} className="flex items-center gap-4 p-4 border border-gray-100 rounded-xl">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                      <i className={`${a.icon} text-teal-600`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">{a.text}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{a.timeAgo}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
